"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperType } from "swiper";
import { TozyMessage } from "@/app/lib/features/tozy/tozy.types";
import ProductCard from "@/app/components/elements/cards/productCard/ProductCard";
import SwiperButton from "@/app/components/elements/buttons/SwiperButton";
import { useHoverGallery } from "@/app/hooks/useHoverGallery";

const CHARS_PER_TICK = 6;
const TICK_MS = 30;

const renderMarkdown = (text: string) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return (
      <span key={i}>
        {part.split("\n").map((line, j, arr) => (
          <span key={j}>
            {line}
            {j < arr.length - 1 && <br />}
          </span>
        ))}
      </span>
    );
  });
};

interface TozyMessageBubbleProps {
  msg: TozyMessage;
  isNew?: boolean;
  icon: string;
}

const TozyMessageBubble = ({ msg, isNew, icon }: TozyMessageBubbleProps) => {
  const fullText = msg.answer;
  const [displayed, setDisplayed] = useState(isNew ? "" : fullText);
  const [done, setDone] = useState(!isNew);
  const [showLeftGradient, setShowLeftGradient] = useState(false);
  const [showRightGradient, setShowRightGradient] = useState(true);
  const hoverGallery = useHoverGallery();

  const updateGradients = (swiper: SwiperType) => {
    setShowLeftGradient(!swiper.isBeginning);
    setShowRightGradient(!swiper.isEnd);
  };

  const hasProducts = msg.products && msg.products.length > 0;
  const idxRef = useRef(isNew ? 0 : fullText.length);

  useEffect(() => {
    if (!isNew) return;

    const id = setInterval(() => {
      idxRef.current = Math.min(idxRef.current + CHARS_PER_TICK, fullText.length);
      setDisplayed(fullText.slice(0, idxRef.current));

      if (idxRef.current >= fullText.length) {
        clearInterval(id);
        setDone(true);
      }
    }, TICK_MS);

    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col gap-2">
      {/* User message */}
      <div className="flex justify-end">
        {/* Kullanıcının kendi mesajı marka renginde; AI'ın balonu nötr gri
            kalıyor ki iki konuşmacı ayırt edilebilsin ve okuma yüzeyi
            sakin dursun. */}
        <div className="max-w-[78%] bg-brand text-white rounded-2xl rounded-tr-sm px-4 py-3 text-sm leading-relaxed">
          {msg.message}
        </div>
      </div>

      {/* AI reply */}
      <div className="flex gap-3 items-start">
        <div className="w-8 h-8 rounded-xl bg-brand flex items-center justify-center shrink-0 overflow-hidden mt-0.5">
          <Image src={icon} alt="Tozzy" width={20} height={20} className="object-contain" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed text-gray-800 max-w-[90%]">
            {renderMarkdown(displayed)}
            {!done && (
              <span className="inline-block w-0.5 h-3.5 bg-brand animate-pulse ml-0.5 align-middle" />
            )}
          </div>
        </div>
      </div>

      {done && hasProducts && (
        <Swiper
          slidesPerView={2.2}
          spaceBetween={8}
          // Kartlar arası boşluklardan da sürüklenebilsin — bkz. ProductSwiper.
          touchEventsTarget="container"
          className="relative w-full"
          onSwiper={updateGradients}
          onSlideChange={updateGradients}
        >
          {msg.products!.map((product) => (
            <SwiperSlide key={product.id} className="h-auto! flex!">
              {/* carousel: kartın tamamı sürüklenebilir — dokunmatikte tek görsel */}
              <ProductCard
                product={product}
                size="sm"
                hideAiButton
                carousel={hoverGallery ? "gallery" : "single"}
              />
            </SwiperSlide>
          ))}

          <div className="absolute z-10 top-1/2 -translate-y-1/2 left-0 w-full flex items-center justify-between px-1 pointer-events-none">
            <SwiperButton position="left" slideMode="group" />
            <SwiperButton position="right" slideMode="group" />
          </div>

          <div className={`absolute top-0 left-0 z-20 w-6 h-full bg-linear-to-r from-white to-transparent pointer-events-none transition-opacity duration-300 ${showLeftGradient ? "opacity-100" : "opacity-0"}`} />
          <div className={`absolute top-0 right-0 z-20 w-6 h-full bg-linear-to-l from-white to-transparent pointer-events-none transition-opacity duration-300 ${showRightGradient ? "opacity-100" : "opacity-0"}`} />
        </Swiper>
      )}
    </div>
  );
};

export default TozyMessageBubble;